import { useContext, useEffect, useMemo, useState } from "react";
import { SsrRequestContext } from "@/lib/ssr-bootstrap";
import { frozenLegalContent, publicLegalContent, type LiveLegalContent } from "@/lib/public-legal-content";

const liveCache = new Map<string, { value: LiveLegalContent | null; capturedAt: number }>();
const freshnessMs = 5 * 60_000;
function cached(key: string) {
  const entry = liveCache.get(key);
  return entry && Date.now() - entry.capturedAt < freshnessMs ? entry : undefined;
}
type State = { key: string; read: "found"; value: LiveLegalContent | null } |
  { key: string; read: "pending" } | { key: string; read: "error"; error: Error };
export function useLiveLegalContent(pageKey: string, locale = "en") {
  const request = useContext(SsrRequestContext);
  const frozen = useMemo(() => frozenLegalContent(request?.seed, pageKey, locale), [request?.seed, pageKey, locale]);
  const key = `${pageKey}:${locale}`;
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const checkFreshness = () => setRefresh(value => value + 1);
    window.addEventListener("focus", checkFreshness);
    return () => window.removeEventListener("focus", checkFreshness);
  }, []);
  const [state, setState] = useState<State>(() => frozen
    ? { key, read: "found", value: frozen.value }
    : cached(key) ? { key, read: "found", value: cached(key)!.value } : { key, read: "pending" });
  useEffect(() => {
    if (frozen) {
      liveCache.set(key, { value: frozen.value, capturedAt: Date.now() });
      setState({ key, read: "found", value: frozen.value });
      return;
    }
    const entry = cached(key);
    if (entry) {
      setState({ key, read: "found", value: entry.value });
      return;
    }
    let cancelled = false;
    const base = `${import.meta.env.BASE_URL || "/"}api`;
    fetch(`${base}/legal-pages/${encodeURIComponent(pageKey)}?locale=${encodeURIComponent(locale)}`)
      .then(async response => {
        if (response.status === 404) {
          const missing: unknown = await response.json();
          if (missing && typeof missing === "object" && "error" in missing && missing.error === "not_found") return null;
        }
        if (!response.ok) throw Object.assign(Error("Required legal source unavailable"), { status: 503 });
        const body: unknown = await response.json();
        if (!body || typeof body !== "object" || !("content" in body))
          throw Object.assign(Error("Required legal source envelope invalid"), { status: 503 });
        return publicLegalContent(body.content);
      })
      .then(value => {
        liveCache.set(key, { value, capturedAt: Date.now() });
        if (!cancelled) setState({ key, read: "found", value });
      })
      .catch(error => {
        // Failed reads are not cached as an absent override.
        if (!cancelled) setState({ key, read: "error", error });
      });
    return () => { cancelled = true; };
  }, [key, pageKey, locale, frozen, refresh]);
  return frozen ? { key, read: "found" as const, value: frozen.value }
    : state.key === key ? state : { key, read: "pending" as const };
}
